import { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useEditor, EditorContent } from '@tiptap/react';
import { StarterKit, TiptapLink, TiptapUnderline, Placeholder } from 'novel';
import TextAlign from '@tiptap/extension-text-align';
import { styled } from '@mui/material/styles';
import clsx from 'clsx';

import AlignableImage from './extensions/AlignableImage';
import RichTextEditorToolbar from './RichTextEditorToolbar';

const RichTextEditor = ({ value, onChange, placeholder, onImageUpload, error }) => {
  const [imageMenuAnchor, setImageMenuAnchor] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);

  const handleImageUpload = async (file) => {
    const publicUrl = await onImageUpload(file);
    return publicUrl;
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: {
          keepMarks: true,
          keepAttributes: false,
          HTMLAttributes: {
            class: 'list-disc',
          },
        },
        orderedList: {
          keepMarks: true,
          keepAttributes: false,
          HTMLAttributes: {
            class: 'list-decimal',
          },
        },
        listItem: {
          HTMLAttributes: {
            class: 'list-item',
          },
        },
      }),
      Placeholder.configure({ placeholder }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      AlignableImage.configure({
        HTMLAttributes: {
          class: 'max-w-full h-auto block my-5 rounded-lg',
        },
      }),
      TiptapLink.configure({
        openOnClick: false,
      }),
      TiptapUnderline,
    ],
    content: value || '',
    onCreate: ({ editor: editorInstance }) => {
      const newHtml = editorInstance.getHTML();
      const json = editorInstance.getJSON();
      onChange(newHtml, json);
    },
    onUpdate: ({ editor: editorInstance }) => {
      // TODO: Uncomment this when we have a way to handle empty content
      // if (editorInstance.isEmpty) {
      //   onChange(null, null);
      //   return;
      // }
      const { state, schema, view } = editorInstance;
      const { doc } = state;
      let { tr } = state;
      let changed = false;

      // Ensure first node is a paragraph
      if (doc.firstChild && doc.firstChild.type.name === 'image') {
        tr = tr.insert(0, schema.nodes.paragraph.create());
        changed = true;
      }
      // Ensure last node is a paragraph
      if (doc.lastChild && doc.lastChild.type.name === 'image') {
        tr = tr.insert(doc.content.size, schema.nodes.paragraph.create());
        changed = true;
      }
      // Ensure no two images are adjacent
      let offset = 0;
      doc.forEach((node, nodeOffset, index) => {
        if (
          node.type.name === 'image' &&
          index + 1 < doc.childCount &&
          doc.child(index + 1).type.name === 'image'
        ) {
          tr = tr.insert(nodeOffset + node.nodeSize, schema.nodes.paragraph.create());
          changed = true;
        }
        offset = nodeOffset + node.nodeSize;
      });

      if (changed) {
        view.dispatch(tr);
      }

      const newHtml = editorInstance.getHTML();
      const json = editorInstance.getJSON();
      onChange(newHtml, json);
    },
    onSelectionUpdate: ({ editor: editorInstance }) => {
      const { state } = editorInstance;
      const { selection } = state;
      if (selection.node && selection.node.type.name === 'image') {
        setSelectedImage(true);
      } else {
        setSelectedImage(null);
      }
    },
  });

  useEffect(() => {
    if (editor) {
      const currentJson = editor.getJSON();
      const valueJson = typeof value === 'object' ? value : null;

      const hasChanged = valueJson
        ? JSON.stringify(currentJson) !== JSON.stringify(valueJson)
        : editor.getHTML() !== value;

      if (hasChanged) {
        editor.commands.setContent(value || '', false);
      }
    }
  }, [value, editor]);

  const handleImageAlign = (align) => {
    if (selectedImage) {
      editor.chain().focus().updateAttributes('image', { align }).run();
    }
    setImageMenuAnchor(null);
  };

  const handleImageSize = (size) => {
    if (selectedImage) {
      editor.chain().focus().updateAttributes('image', { width: size }).run();
    }
    setImageMenuAnchor(null);
  };

  if (!editor) {
    return null;
  }

  return (
    <Box>
      <Box
        className={clsx('border rounded-4 px-12 py-10', {
          'border-typography-danger': error,
          'border-neutral-300': !error,
        })}
      >
        <Box className="sticky top-0 z-10 bg-white border-b">
          <RichTextEditorToolbar
            editor={editor}
            onImageUpload={handleImageUpload}
            selectedImage={selectedImage}
            imageMenuAnchor={imageMenuAnchor}
            setImageMenuAnchor={setImageMenuAnchor}
            handleImageAlign={handleImageAlign}
            handleImageSize={handleImageSize}
          />
        </Box>
        <StyledEditorContent className="min-h-[200px]">
          <EditorContent editor={editor} />
        </StyledEditorContent>
      </Box>
      {error && <Typography className="text-typography-danger text-12 mt-4">{error}</Typography>}
    </Box>
  );
};

const StyledEditorContent = styled(Box)({
  '& .ProseMirror': {
    outline: 'none',
    minHeight: '200px',
    padding: '8px',
    cursor: 'text',
    '&:focus': {
      outline: 'none',
    },
  },
  '& .ProseMirror ul': {
    listStyleType: 'disc',
    paddingLeft: '1.5rem',
    marginTop: '0.5rem',
    marginBottom: '0.5rem',
  },
  '& .ProseMirror ol': {
    listStyleType: 'decimal',
    paddingLeft: '1.5rem',
    marginTop: '0.5rem',
    marginBottom: '0.5rem',
  },
  '& .ProseMirror li': {
    marginTop: '0.25rem',
    marginBottom: '0.25rem',
  },
  '& .ProseMirror ul ul, & .ProseMirror ol ol, & .ProseMirror ul ol, & .ProseMirror ol ul': {
    marginTop: '0.25rem',
    marginBottom: '0.25rem',
  },
  '& .ProseMirror img': {
    display: 'block',
    clear: 'both',
    margin: '1rem auto',
  },
  '& .ProseMirror img.ProseMirror-selectednode': {
    outline: '2px solid #489b82',
    outlineOffset: '2px',
  },
});

export default RichTextEditor;
